import {
	DEFAULT_OPENREPLAY_SCRIPT_URL,
	OPENREPLAY_SCRIPT_ID,
} from './constants';
import { openReplaySessionContext } from './context';
import { sanitizeOpenReplayNetworkData } from './sanitize';
import {
	OpenReplayAPI,
	OpenReplayInitOptions,
	OpenReplayQueue,
	OpenReplaySessionContext,
} from './types';

enum OpenReplayCommand {
	Start = 0,
	Stop = 1,
	SetUserId = 2,
	SetAnonymousUserId = 3,
	SetMetadata = 4,
	Event = 5,
	Issue = 6,
}

let initialized = false;

export function safeOpenReplayCall(callback: () => void): void {
	try {
		callback();
	} catch (error) {
		// OpenReplay must never affect quiz flow.
	}
}

export function getOpenReplay(): OpenReplayAPI | undefined {
	return window.__OPENREPLAY__ || window.OpenReplay;
}

function isLocalhost(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}

	return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function printOpenReplayState(stage: string): void {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return;
	}

	const script = document.getElementById(OPENREPLAY_SCRIPT_ID) as HTMLScriptElement | null;
	const tracker = getOpenReplay();

	console.info('[OpenReplay]', stage, {
		hasProjectKey: !!process.env.REACT_APP_OPENREPLAY_PROJECT_KEY,
		enabledFlag: process.env.REACT_APP_OPENREPLAY_ENABLED,
		hasIngestPoint: !!process.env.REACT_APP_OPENREPLAY_INGEST_POINT,
		hasScript: !!script,
		scriptSrc: script?.src,
		hasWindowOpenReplay: !!window.OpenReplay,
		hasInternalOpenReplay: !!window.__OPENREPLAY__,
		isActive: tracker?.isActive?.(),
		hasSessionToken: !!tracker?.getSessionToken?.(),
		doNotTrack: navigator.doNotTrack,
		isLocalhost: isLocalhost(),
	});
}

function isOpenReplayEnabled(): boolean {
	return !!process.env.REACT_APP_OPENREPLAY_PROJECT_KEY
		&& process.env.REACT_APP_OPENREPLAY_ENABLED !== '0';
}

function getOpenReplayInitOptions(): OpenReplayInitOptions {
	const projectKey = process.env.REACT_APP_OPENREPLAY_PROJECT_KEY as string;
	const ingestPoint = process.env.REACT_APP_OPENREPLAY_INGEST_POINT;
	const disableSecureMode = isLocalhost()
		|| process.env.REACT_APP_OPENREPLAY_DISABLE_SECURE_MODE === '1';

	return {
		projectKey,
		...(ingestPoint ? { ingestPoint } : {}),
		__DISABLE_SECURE_MODE: disableSecureMode,
		respectDoNotTrack: process.env.REACT_APP_OPENREPLAY_RESPECT_DNT === '1',
		network: {
			capturePayload: true,
			sanitizer: sanitizeOpenReplayNetworkData,
		},
	};
}

function createQueuedOpenReplayApi(
	initOptions: OpenReplayInitOptions,
	startOptions: OpenReplaySessionContext,
): OpenReplayQueue {
	const queue = [
		startOptions,
		undefined,
		initOptions,
		[OpenReplayCommand.Start, startOptions],
	] as OpenReplayQueue;

	console.info('[OpenReplay]', 'creating session', {
		hasProjectKey: !!initOptions.projectKey,
		hasIngestPoint: !!initOptions.ingestPoint,
		hasUserId: !!startOptions.userID,
		hasQuizId: !!startOptions.metadata.quizId,
		disableSecureMode: !!initOptions.__DISABLE_SECURE_MODE,
		respectDoNotTrack: !!initOptions.respectDoNotTrack,
	});

	queue.start = (options?: unknown) => {
		console.info('[OpenReplay]', 'start called', {
			hasStartOptions: !!options,
			startOptionKeys: options && typeof options === 'object' && !Array.isArray(options)
				? Object.keys(options)
				: [],
		});
		queue.push([OpenReplayCommand.Start, options]);
	};
	queue.stop = () => queue.push([OpenReplayCommand.Stop]);
	queue.setUserID = (id: string) => queue.push([OpenReplayCommand.SetUserId, id]);
	queue.setUserAnonymousID = (id: string) => {
		queue.push([OpenReplayCommand.SetAnonymousUserId, id]);
	};
	queue.setMetadata = (key: string, value: string) => {
		queue.push([OpenReplayCommand.SetMetadata, key, value]);
	};
	queue.event = (key: string, payload?: unknown, issue?: boolean) => {
		queue.push([OpenReplayCommand.Event, key, payload, issue]);
	};
	queue.issue = (key: string, payload?: unknown) => {
		queue.push([OpenReplayCommand.Issue, key, payload]);
	};
	queue.isActive = () => false;
	queue.getSessionToken = () => undefined;

	return queue;
}

function appendOpenReplayScript(scriptUrl: string): void {
	const script = document.createElement('script');

	script.id = OPENREPLAY_SCRIPT_ID;
	script.src = scriptUrl;
	script.async = true;
	script.onload = () => {
		printOpenReplayState('script loaded');
		window.setTimeout(() => printOpenReplayState('post-load check'), 1000);
	};
	script.onerror = () => {
		initialized = false;
		printOpenReplayState('script failed');
		script.remove();
	};

	document.head.appendChild(script);
	printOpenReplayState('script appended');
}

export function initOpenReplayLoader(): void {
	if (initialized || typeof window === 'undefined' || typeof document === 'undefined') {
		return;
	}

	if (!isOpenReplayEnabled()) {
		printOpenReplayState('disabled');
		return;
	}

	safeOpenReplayCall(() => {
		initialized = true;

		if (document.getElementById(OPENREPLAY_SCRIPT_ID)) {
			printOpenReplayState('script already present');
			return;
		}

		const scriptUrl = process.env.REACT_APP_OPENREPLAY_SCRIPT_URL
			|| DEFAULT_OPENREPLAY_SCRIPT_URL;

		window.OpenReplay = createQueuedOpenReplayApi(
			getOpenReplayInitOptions(),
			openReplaySessionContext,
		);
		printOpenReplayState('queue created');
		appendOpenReplayScript(scriptUrl);
	});
}
